import { zodResolver } from '@hookform/resolvers/zod';
import {
  CrmFieldMatch,
  FieldMappingCreateFieldType,
  FieldMappingCreateFormSchema,
  FieldMappingCreateFormType,
  FieldMappingCreateReqType,
  FieldMappingObjectType,
  FieldMappingSystemType,
  FieldMappingViaFieldType,
} from '@monetize/types/app';
import hasValue from 'lodash/has';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import startCase from 'lodash/startCase';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { BsArrowRight } from 'react-icons/bs';
import { MdClose } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../../api/axios';
import {
  CrmFieldMappingDirection,
  useCreateConnectorFieldMapping,
} from '../../../../../../api/fieldMappingService';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MAlert,
  MButton,
  MCustomIconButton,
  MDivider,
  MFlex,
  MFormField,
  MIcon,
  MText,
} from '../../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../../components/Monetize/MSettingAccordionItem';
import {
  HS_VALIDATION_IGNORABLE_FIELD_API_NAMES,
  MN_QUOTE_DEPENDENT_FIELD_API_NAMES,
  MN_REFERENCE_TYPE_FIELD_API_NAMES,
} from '../../../../../../constants/crmFieldMapping';
import { usePrompt } from '../../../../../../hooks/usePrompt';
import { useRecordState } from '../../../../../../hooks/useRecordState';
import { logger } from '../../../../../../services/logger';
import { toTitleCase } from '../../../../../../utils';
import { transformCrmFields } from '../../../../../../utils/crmFieldMapping';
import { CrmFieldMappingDropdown } from './CrmFieldMappingDropdown';

type CrmFieldMappingAccordionProps = {
  system: FieldMappingSystemType;
  matchedFields: CrmFieldMatch;
  userDefinedFields: FieldMappingCreateReqType[];
  mnObjectType: FieldMappingObjectType;
  /** Human friendly label, defaults to {mnObjectType} (capitalized) */
  mnObjectTypeLabel?: string;
  crmObjectType: FieldMappingObjectType;
  /** Human friendly label, defaults to {crmObjectType} (capitalized) */
  crmObjectTypeLabel?: string;
  /**
   * `crmToMn` (default) maps CRM fields into MonetizeNow; `mnToCrm` maps
   * MonetizeNow fields out to the CRM (e.g. Product/Offering -> Product2).
   */
  direction?: CrmFieldMappingDirection;
  /** Shows the saved mappings without letting them be changed. */
  isReadOnly?: boolean;
};

export const CrmFieldMappingAccordion = ({
  system,
  matchedFields,
  userDefinedFields,
  mnObjectType,
  mnObjectTypeLabel = mnObjectType,
  crmObjectType,
  crmObjectTypeLabel = crmObjectType,
  direction = 'crmToMn',
  isReadOnly = false,
}: CrmFieldMappingAccordionProps) => {
  const isMnToCrm = direction === 'mnToCrm';
  // The id is derived from the object pair alone, so the same pair rendered in
  // both directions (Account -> Account today) would collide — and the Save
  // button targets its form by id (`form={formId}`), so a duplicate submits the
  // wrong form. Every outbound id carries the direction, keeping ids unique by
  // construction rather than by which pairs happen to render.
  const formId = `${system}-${crmObjectType}-${mnObjectType}${
    isMnToCrm ? '-mntocrm' : ''
  }-form`.toLowerCase();
  const hasRefLookUp =
    (crmObjectType === 'deals' && mnObjectType === 'Quote') ||
    (crmObjectType === 'Opportunity' && mnObjectType === 'Quote');

  const crmFields = transformCrmFields(matchedFields, 'crm');
  const mnFields = transformCrmFields(matchedFields, 'monetize');
  const systemLabel = useMemo(() => startCase(system), []);
  const mnFieldLabel = 'MonetizeNow Field';
  const crmFieldLabel = `${systemLabel} Field`;

  // The form's `source`/`target` map to the left/right dropdowns. For the
  // default crmToMn direction the CRM field is the source; for mnToCrm the
  // MonetizeNow field is the source.
  const sourceFields = isMnToCrm ? mnFields : crmFields;
  const targetFields = isMnToCrm ? crmFields : mnFields;
  const sourceLabel = isMnToCrm ? mnFieldLabel : crmFieldLabel;
  const targetLabel = isMnToCrm ? crmFieldLabel : mnFieldLabel;
  const leftObjectTypeLabel = isMnToCrm
    ? mnObjectTypeLabel
    : crmObjectTypeLabel;
  const rightObjectTypeLabel = isMnToCrm
    ? crmObjectTypeLabel
    : mnObjectTypeLabel;

  const {
    setValue,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitSuccessful, dirtyFields, isValid },
  } = useForm<FieldMappingCreateFormType>({
    mode: 'onSubmit',
    resolver: zodResolver(FieldMappingCreateFormSchema),
    defaultValues: {
      [formId]: userDefinedFields,
    },
  });

  const {
    state: alertState,
    addItem,
    removeItem,
  } = useRecordState<number, boolean>({});
  const {
    state: errorState,
    addItem: addError,
    removeItem: removeError,
  } = useRecordState<
    string,
    {
      source: {
        message?: string;
      };
      target: {
        message?: string;
      };
    }
  >({});

  const { fields, append, remove } = useFieldArray({
    name: formId,
    control,
  });

  const { mutate: createFieldMapping, isPending: isLoading } =
    useCreateConnectorFieldMapping(system, 'crm', {
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  usePrompt(
    'There are unsaved changed, do you want to discard these changes?',
    isDirty,
  );
  const onSubmit = (data: FieldMappingCreateFormType) =>
    createFieldMapping({
      sourceEntity: isMnToCrm ? mnObjectType : crmObjectType,
      targetEntity: isMnToCrm ? crmObjectType : mnObjectType,
      direction,
      payloads: data[formId],
    });

  const onError = (err: any) => {
    const errors = err[formId];
    if (!isEmpty(errors) && isArray(errors)) {
      errors.forEach((error, idx) => {
        const errorMessage = {
          source: {},
          target: {},
        };
        if (error && error.source) {
          errorMessage.source = {
            message: `${sourceLabel} is required`,
          };
        }
        if (error && error.target) {
          errorMessage.target = {
            message: `${targetLabel} is required`,
          };
        }
        if (!isEmpty(errorMessage)) {
          addError(`${formId}_${idx}`, errorMessage);
        } else {
          removeError(`${formId}_${idx}`);
        }
      });
    }
  };
  const onAddMapping = () =>
    append(
      {
        mode: 'by_value',
        source: {},
        target: {},
      } as FieldMappingCreateReqType,
      {
        shouldFocus: true,
      },
    );

  const handleOnClear = (idx: number, side: 'source' | 'target') => {
    setValue(`${formId}.${idx}.${side}`, {} as FieldMappingCreateFieldType, {
      shouldValidate: true,
    });
    removeItem(idx);
    removeError(`${formId}_${idx}`);
  };

  const setAlert = (ev: any, idx: number) => {
    const { fieldApiName } = ev as FieldMappingCreateFieldType;
    if (hasRefLookUp && MN_QUOTE_DEPENDENT_FIELD_API_NAMES.has(fieldApiName)) {
      addItem(idx, true);
    } else {
      removeItem(idx);
    }
  };

  const changeMode = (ev: any, idx: number) => {
    const { fieldApiName } = ev as FieldMappingCreateFieldType;
    if (hasRefLookUp && MN_REFERENCE_TYPE_FIELD_API_NAMES.has(fieldApiName)) {
      const [firstPart, secondPart] = fieldApiName.split('.');
      setValue(`${formId}.${idx}.mode`, 'by_reference_with_lookup_filter');
      switch (firstPart) {
        case 'legalEntityId':
          setValue(
            `${formId}.${idx}.via`,
            {
              objectType: 'LegalEntity',
              fieldApiName: secondPart,
            } as FieldMappingViaFieldType,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
          break;
        case 'contacts':
          setValue(
            `${formId}.${idx}.via`,
            {
              objectType: 'Contact',
              fieldApiName: 'email',
            } as FieldMappingViaFieldType,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
          break;
        default:
          logger.error('case out of bound');
          break;
      }
    } else {
      setValue(`${formId}.${idx}.mode`, 'by_value');
      setValue(`${formId}.${idx}.via`, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const formFields = watch(formId);

  const validateData = (idx: number) => {
    const { source, target } = formFields[idx];
    if (
      !isEmpty(source) &&
      !isEmpty(target) &&
      !HS_VALIDATION_IGNORABLE_FIELD_API_NAMES.has(source.fieldApiName) &&
      source.dataType !== target.dataType
    ) {
      addError(`${formId}_${idx}`, {
        source: {
          message: `Data type doesn't match: ${toTitleCase(source.dataType)}`,
        },
        target: {
          message: `Data type doesn't match: ${toTitleCase(target.dataType)}`,
        },
      });
    } else {
      removeError(`${formId}_${idx}`);
    }
  };

  const getVariant = (idx: number, side: 'source' | 'target') => {
    if (isEmpty(dirtyFields)) {
      return undefined;
    }
    const dirtyField = dirtyFields[formId];
    if (!dirtyField || !dirtyField[idx] || !hasValue(dirtyField[idx], side)) {
      return undefined;
    }
    if (isObject(dirtyField[idx][side]) && isEmpty(dirtyField[idx][side])) {
      return 'unsaved';
    }
    return (dirtyField[idx][side] as unknown as boolean) === true
      ? 'unsaved'
      : undefined;
  };

  const unsavedChangeAlert = (
    <MAlert
      type="warning"
      alertProps={{ px: 2, py: 1 }}
      alertIconProps={{ width: 4, height: 4 }}
      alertDescriptionProps={{ fontSize: 'sm' }}
    >
      Unsaved Changes
    </MAlert>
  );

  useEffect(() => {
    userDefinedFields.forEach((item, index) => {
      if (
        hasRefLookUp &&
        MN_QUOTE_DEPENDENT_FIELD_API_NAMES.has(item.target.fieldApiName)
      ) {
        addItem(index, true);
      }
      validateData(index);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE: https://github.com/react-hook-form/react-hook-form/issues/3097#issuecomment-1310537506
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true });
    }
  }, [isSubmitSuccessful, reset]);

  const isFormValid = isValid && isEmpty(errorState);

  return (
    <MAccordion collapsible w="100%">
      <MSettingAccordionItem
        renderAccordionButton={({ isExpanded }) => {
          return (
            <MAccordionCustomButton
              isExpanded={isExpanded}
              label=""
              customLabel={
                <MFlex align="center" gap={2} ml={1}>
                  <MText
                    fontWeight="semibold"
                    fontSize="lg"
                    textTransform="capitalize"
                  >
                    {leftObjectTypeLabel}
                  </MText>
                  <MIcon as={BsArrowRight} />
                  <MText
                    fontWeight="semibold"
                    fontSize="lg"
                    textTransform="capitalize"
                  >
                    {rightObjectTypeLabel}
                  </MText>
                </MFlex>
              }
            >
              {isDirty && !isExpanded && unsavedChangeAlert}
              <MAccordionCustomButtonItem
                align="left"
                label="Fields Mapped"
                ml={4}
                value={userDefinedFields.length}
                isExpanded={isExpanded}
                hideWhenExpanded
              />
            </MAccordionCustomButton>
          );
        }}
      >
        <MFlex direction="column" align="flex-start">
          <MFlex
            as="form"
            direction="column"
            gap={4}
            id={formId}
            onSubmit={handleSubmit(onSubmit, onError)}
            w="full"
            mt={2}
          >
            {fields.map((ff, idx) => {
              return (
                <MFlex direction="column" key={ff.id} gap={2}>
                  <MFlex gap={4} align="flex-end" justify="space-between">
                    <MFormField
                      label={idx === 0 ? sourceLabel : undefined}
                      error={errorState[`${formId}_${idx}`]?.source}
                    >
                      <Controller
                        name={`${formId}.${idx}.source`}
                        control={control}
                        render={({ field: { onChange, ...rest } }) => (
                          <CrmFieldMappingDropdown
                            placeholder={sourceLabel}
                            fields={sourceFields}
                            fieldProps={rest}
                            index={idx}
                            handleOnClear={handleOnClear}
                            onChange={onChange}
                            validateData={validateData}
                            getVariant={getVariant}
                            changeMode={changeMode}
                            setAlert={setAlert}
                            side="source"
                            isMNField={isMnToCrm}
                            isDisabled={isReadOnly}
                          />
                        )}
                      />
                    </MFormField>
                    <MIcon as={BsArrowRight} mb={2.5} />
                    <MFormField
                      label={idx === 0 ? targetLabel : undefined}
                      error={errorState[`${formId}_${idx}`]?.target}
                    >
                      <Controller
                        name={`${formId}.${idx}.target`}
                        control={control}
                        render={({ field: { onChange, ...rest } }) => (
                          <CrmFieldMappingDropdown
                            placeholder={targetLabel}
                            fields={targetFields}
                            fieldProps={rest}
                            index={idx}
                            handleOnClear={handleOnClear}
                            onChange={onChange}
                            validateData={validateData}
                            getVariant={getVariant}
                            changeMode={changeMode}
                            setAlert={setAlert}
                            side="target"
                            isMNField={!isMnToCrm}
                            isDisabled={isReadOnly}
                          />
                        )}
                      />
                    </MFormField>
                    {!isReadOnly && (
                      <MCustomIconButton
                        type="button"
                        icon={MdClose}
                        aria-label="Remove mapping"
                        containerSize={6}
                        btnSize={4}
                        onClick={() => {
                          remove(idx);
                          removeItem(idx);
                          removeError(`${formId}_${idx}`);
                        }}
                      />
                    )}
                  </MFlex>
                  {alertState[idx] && (
                    <MAlert
                      alertDescriptionProps={{ fontSize: '14px' }}
                      alertIconProps={{ w: 4, h: 4 }}
                      alertProps={{ py: 0, mr: 8 }}
                    >
                      Field set on Quote creation. Not updated post Quote
                      creation.
                    </MAlert>
                  )}
                </MFlex>
              );
            })}
          </MFlex>
          {!isReadOnly && (
            <MButton
              variant="tertiary"
              my={2}
              onClick={onAddMapping}
              minW="auto"
              type="button"
            >
              + Add Mapping
            </MButton>
          )}
        </MFlex>
        {!isReadOnly && (
          <>
            <MDivider />
            <MFlex
              direction="row"
              align="center"
              justify="right"
              gap={4}
              mt={4}
            >
              {isDirty && unsavedChangeAlert}
              <MButton
                variant="primary"
                type="submit"
                form={formId}
                isLoading={isLoading}
                isDisabled={!isFormValid || !isDirty || isLoading}
              >
                Save
              </MButton>
            </MFlex>
          </>
        )}
      </MSettingAccordionItem>
    </MAccordion>
  );
};
